import React, { useState } from 'react';
import classnames from 'classnames';

import { Grid, IconButton, Chip, Accordion, AccordionSummary, AccordionDetails, Typography, Checkbox } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { Icon, Translate, RatingIcons } from '@hydra/common/components';

import { sendEvent } from '@hydra/utils/gtm';
import { ExecutorMapI18nType, IndexedKeysType } from '@hydra/types/marketing-checklist';

const mapImportanceI18n: IndexedKeysType = {
  1: 'criterion_importance1',
  2: 'criterion_importance2',
  3: 'criterion_importance3'
};

const mapSolvabilityI18n: IndexedKeysType = {
  1: 'criterion_solvability1',
  2: 'criterion_solvability2',
  3: 'criterion_solvability3'
};

const mapExecutorI18n: ExecutorMapI18nType = {
  marketer: 'todo_executor_mar',
  designer: 'todo_executor_des',
  developer: 'todo_executor_dev'
};

const useStyles = makeStyles((theme) => ({
  details: {
    flexDirection: 'column',
    paddingTop: 0
  },
  toolbar: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(0, 3),
    height: 'auto',
    minHeight: 'inherit',
    [theme.breakpoints.up('sm')]: {
      height: theme.spacing(5),
      flexDirection: 'row',
      borderBottom: `1px solid ${theme.palette.divider}`
    }
  },
  toolbarItem: {
    margin: theme.spacing(0.5, 2, 0.5, 0),
    display: 'flex',
    height: theme.spacing(3)
  },
  fontSizeSmall: {
    fontSize: theme.typography.fontSizeSmall
  },
  summaryRoot: {
    height: theme.spacing(6),
    justifyContent: 'space-between',
    paddingLeft: theme.spacing(1)
  },
  summaryContent: {
    overflow: 'hidden'
  },
  summaryContainer: {
    padding: theme.spacing(1.5, 3)
  },
  borderRight: {
    [theme.breakpoints.up('sm')]: {
      borderRight: `1px solid ${theme.palette.divider}`
    }
  },
  checkbox: {
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden'
  },
  checkboxChecked: {
    textDecoration: 'line-through',
    color: theme.palette.grey[500]
  },
  chip: {
    backgroundColor: theme.palette.background.secondary,
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.fontSizeTiny,
    marginLeft: theme.spacing(1.25),
    marginTop: theme.spacing(0.25) * -1
  },
  getItDone: {
    '& a': {
      color: theme.palette.primary.main
    }
  },
  quicktipContainer: {
    fontSize: theme.typography.fontSizeSmall,
    '& li': {
      margin: 0,
      padding: `${theme.spacing(1)}px 0`,
      listStyleType: 'none'
    },
    '& a': {
      color: theme.palette.primary.main
    }
  }
}));

export interface TaskInterface {
  category: string,
  checked: boolean,
  executor: string,
  id: string,
  importance: number,
  solvability: number
}

interface TaskItem {
  task: TaskInterface,
  onChange: () => void
}

export default function TaskItem ({ task, onChange }: TaskItem) {
  const classes = useStyles();
  const [busy, setBusy] = useState(false);
  const { id, solvability, executor, importance, checked, category } = task;

  const handleOnCheck = async (e: React.ChangeEvent<HTMLInputElement>) => {
    sendEvent(`marketingchecklist-tip-${e.target.checked ? 'check' : 'uncheck'}-checkbox`, {
      tip: id
    });
    try {
      setBusy(true);
      await onChange();
    } catch (err) {
      setBusy(false);
      throw err;
    } finally {
      setBusy(false);
    }
  };

  const handleChange = (event: React.ChangeEvent<{}>, expanded: boolean) => {
    if ((event.target as HTMLInputElement).id === `${id}-expand-icon`) {
      sendEvent(`marketingchecklist-tip-${expanded ? 'open' : 'close'}-chevron`, {
        tip: id
      });
    } else {
      sendEvent(`marketingchecklist-tip-${expanded ? 'open' : 'close'}`, {
        tip: id
      });
    }
  };

  return (
    <Accordion
      elevation={0}
      TransitionProps={{ unmountOnExit: true }}
      onChange={handleChange}
    >
      <AccordionSummary
        id={`${id}-header`}
        expandIcon={<Icon type='chevron-down' id={`${id}-expand-icon`} />}
        aria-controls={`${id}-content`}
        classes={{
          root: classes.summaryRoot,
          content: classes.summaryContent
        }}
      >
        <div
          className={classnames(
            classes.checkbox,
            {
              [classes.checkboxChecked]: checked
            }
          )}
        >
          <div>
            {
              busy
                ? (
                  <IconButton disabled>
                    <Icon type='loader' size='sm' />
                  </IconButton>
                  )
                : (
                  <Checkbox
                    checked={checked}
                    onChange={handleOnCheck}
                    name='checked'
                    onClick={e => e.stopPropagation()}
                    onFocus={e => e.stopPropagation()}
                  />
                  )
            }
          </div>
          <Typography noWrap>
            <Translate content={`wip_${id}`} />
          </Typography>
        </div>
      </AccordionSummary>
      <AccordionDetails
        classes={{ root: classes.details }}
      >
        <div className={classes.toolbar}>
          <div className={classes.toolbarItem}>
            <div>
              <RatingIcons
                type='importance'
                size='xs'
                rating={importance}
                key='importance'
              />
            </div>
            <Typography className={classes.fontSizeSmall}>
              <Translate content={mapImportanceI18n[importance.toString() as keyof IndexedKeysType]} />
            </Typography>
          </div>
          <div className={classes.toolbarItem}>
            <div>
              <RatingIcons
                type='solvability'
                size='xs'
                rating={solvability}
                key='solvability'
              />
            </div>
            <Typography className={classes.fontSizeSmall}>
              <Translate content={mapSolvabilityI18n[solvability.toString() as keyof IndexedKeysType]} />
            </Typography>
          </div>
          <div className={classes.toolbarItem}>
            <Typography className={classes.fontSizeSmall}>
              <Translate content='todo_for' />
              {' '}
              <Translate content={mapExecutorI18n[executor.toString() as keyof ExecutorMapI18nType]} />
            </Typography>
            <Chip
              className={classes.chip}
              size='small'
              label={<Translate content={category} />}
            />
          </div>
        </div>
        <Grid
          container
          item
          direction='row'
        >
          <Grid
            item
            xs={12}
            sm={6}
            className={classnames(
              classes.summaryContainer,
              classes.borderRight
            )}
          >
            <Typography
              variant='h4'
              gutterBottom
            >
              <Translate content='why_important' />
            </Typography>
            <Typography
              paragraph
              className={classes.fontSizeSmall}
            >
              <Translate content={`wip_${id}_summary`} />
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            className={classes.summaryContainer}
          >
            <Grid>
              <Typography
                variant='h4'
                gutterBottom
              >
                <Translate content='get_it_done' />
              </Typography>
              <Typography
                paragraph
                className={classnames(
                  classes.fontSizeSmall,
                  classes.getItDone
                )}
              >
                <Translate content={`wip_${id}_text`} />
              </Typography>
            </Grid>
            <Grid>
              <Typography
                variant='h4'
                gutterBottom
              >
                <Translate content='todo_external_resources' />
              </Typography>
              <Typography
                paragraph
                className={classnames(
                  classes.fontSizeSmall,
                  classes.quicktipContainer
                )}
              >
                <Translate content={`wip_${id}_links`} />
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
}
